import { rendersEntityView } from '@bliks-io/ui';
import {
  type ChatFilePart,
  type ChatGenerateDocumentPart,
  type ChatRenderChartPart,
  type ChatRenderEntityViewPart,
  type ChatTextPart,
  type ChatToolPartState
} from '@bliks-io/contracts';

/**
 * What the transcript considers a renderable message part.
 *
 * Both chats receive AI SDK parts of many kinds (`step-start`, tool calls, …)
 * and only a subset reaches the screen. This module is the single place that
 * decides which — the conversion adapter, the transcript, the message row and
 * the tests' scripted transport all ask here.
 *
 * Named `Transcript*` rather than `Chat*` because `@bliks-io/contracts` already
 * owns `ChatMessagePart` / `ChatFilePart` for the PERSISTED envelope; both are
 * live inside this folder, so one identifier for two shapes would let an
 * auto-completed import silently widen an invariant.
 */

/** A streamed text part — identical to the persisted one, so it is not restated. */
export type TranscriptTextPart = ChatTextPart;

/**
 * A persisted tool part as the transport STREAMS it: the wire additionally
 * carries the in-flight and failed states and, before the call is registered, no
 * id at all. Widening the persisted shape keeps the streaming version from
 * drifting from what is ultimately stored.
 *
 * `output` stays `unknown` on both sides: the renderer parses it against the
 * tool's own output schema and shows an error card if it does not hold, so a
 * malformed live payload can never reach recharts or become a link.
 */
type StreamingToolPart<TPersisted> = Omit<TPersisted, 'state' | 'toolCallId'> & {
  state: ChatToolPartState;
  toolCallId?: string;
};

/** A `renderChart` tool part, in any state the stream can put it in. */
export type TranscriptChartPart = StreamingToolPart<ChatRenderChartPart>;

/** A `generateDocument` tool part, in any state the stream can put it in. */
export type TranscriptDocumentPart = StreamingToolPart<ChatGenerateDocumentPart>;

/** A `renderEntityView` tool part, in any state the stream can put it in. */
export type TranscriptEntityViewPart = StreamingToolPart<ChatRenderEntityViewPart>;

/**
 * A document the user attached to a turn (plan 012).
 *
 * The one part that cannot be derived from its persisted counterpart: the same
 * type arrives from two directions, so EVERY locator is optional here while
 * `ChatFilePart` requires `filename`, `mediaType` and `storageKey`. A message
 * just sent carries only what the browser knew; one reloaded from history
 * carries the backend's `storageKey`.
 */
export interface TranscriptFilePart {
  type: 'file';
  filename?: ChatFilePart['filename'];
  mediaType?: string;
  /** Object-storage handle of a persisted attachment. */
  storageKey?: ChatFilePart['storageKey'];
  /**
   * The AI SDK's own locator, which its file part requires. Ours is always
   * empty: the object is private and ephemeral, so there is no URL the browser
   * could hold — see `convertHistoryToUIMessages`.
   */
  url?: string;
}

/** Any part an AI SDK message can carry, renderable or not. */
export type TranscriptPart =
  | TranscriptTextPart
  | TranscriptChartPart
  | TranscriptDocumentPart
  | TranscriptEntityViewPart
  | TranscriptFilePart
  | { type: string };

export function isTextPart(part: TranscriptPart): part is TranscriptTextPart {
  return part.type === 'text';
}

export function isChartPart(part: TranscriptPart): part is TranscriptChartPart {
  return part.type === 'tool-renderChart';
}

export function isDocumentPart(part: TranscriptPart): part is TranscriptDocumentPart {
  return part.type === 'tool-generateDocument';
}

export function isEntityViewPart(part: TranscriptPart): part is TranscriptEntityViewPart {
  return part.type === 'tool-renderEntityView';
}

export function isFilePart(part: TranscriptPart): part is TranscriptFilePart {
  return part.type === 'file';
}

/**
 * Whether the renderer knows how to draw this kind of part. Kind only — a part
 * can be renderable and still be visually empty while it streams.
 */
export function isRenderablePart(
  part: TranscriptPart
): part is
  | TranscriptTextPart
  | TranscriptChartPart
  | TranscriptDocumentPart
  | TranscriptEntityViewPart
  | TranscriptFilePart {
  return (
    isTextPart(part) ||
    isChartPart(part) ||
    isDocumentPart(part) ||
    isEntityViewPart(part) ||
    isFilePart(part)
  );
}

/**
 * Whether this part would actually put something on screen. An assistant turn
 * whose only text part is still whitespace belongs to the thinking indicator.
 * Chart, document and file parts count in every state — each draws a placeholder
 * or a chip before its payload lands. An entity view is the one part that can be
 * renderable in KIND and still draw nothing, so it is asked rather than assumed.
 *
 * The regex avoids `trim()`, which would copy a multi-kilobyte, still-streaming
 * string on every token just to answer a yes/no question.
 */
export function hasVisibleContent(part: TranscriptPart): boolean {
  if (isChartPart(part) || isDocumentPart(part) || isFilePart(part)) return true;
  if (isEntityViewPart(part)) return rendersEntityView(part);
  return isTextPart(part) && /\S/.test(part.text);
}

/** The markdown document a turn's text parts form once streaming splits them. */
export function textFromParts(parts: TranscriptPart[] | undefined): string {
  return (
    parts
      ?.filter(isTextPart)
      .map((part) => part.text)
      .join('') ?? ''
  );
}
